// OLE2 Compound File Binary Format (MS-CFB) 파서
// HWP 5.0 파일은 이 포맷 위에 구축됨

import { HWPXError } from '../../errors.js';

const HEADER_SIZE = 512;
const DIR_ENTRY_SIZE = 128;
const ENDOFCHAIN = 0xfffffffe;
const FREESECT = 0xffffffff;
const DIFSECT = 0xfffffffc;
const FATSECT = 0xfffffffd;

export interface OleEntry {
  name: string;
  type: number; // 1=storage, 2=stream, 5=root
  size: number;
  startSector: number;
  childId: number;
  leftSiblingId: number;
  rightSiblingId: number;
}

export interface OleFile {
  entries: OleEntry[];
  getStream(name: string): Uint8Array;
  getStreamByEntry(entry: OleEntry): Uint8Array;
  listStreams(): string[];
}

export function parseOle(data: Uint8Array): OleFile {
  if (data.length < HEADER_SIZE) {
    throw new HWPXError('File too small for OLE format');
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  // Magic number 검증
  if (view.getUint32(0, true) !== 0xe011cfd0 || view.getUint32(4, true) !== 0xa1b11ae1) {
    throw new HWPXError('Invalid OLE magic number');
  }

  const sectorSize = 1 << view.getUint16(30, true); // 보통 512
  const miniSectorSize = 1 << view.getUint16(32, true); // 보통 64
  const fatSectors = view.getInt32(44, true);
  const firstDirSector = view.getInt32(48, true);
  const miniStreamCutoff = view.getUint32(56, true); // 보통 4096
  const firstMiniFatSector = view.getInt32(60, true);
  const miniSectorCount = view.getInt32(64, true);
  const firstDifatSector = view.getInt32(68, true);
  const difatCount = view.getInt32(72, true);

  // DIFAT (헤더에 109개 + 추가 DIFAT 섹터)
  const difat: number[] = [];
  for (let i = 0; i < 109 && i < fatSectors; i++) {
    difat.push(view.getInt32(76 + i * 4, true));
  }

  // 추가 DIFAT 섹터 읽기
  let difatSector = firstDifatSector;
  while (difatSector >= 0 && difatSector !== ENDOFCHAIN && difat.length < fatSectors) {
    const offset = sectorOffset(difatSector, sectorSize);
    const entriesPerSector = sectorSize / 4 - 1;
    for (let i = 0; i < entriesPerSector && difat.length < fatSectors; i++) {
      difat.push(view.getInt32(offset + i * 4, true));
    }
    difatSector = view.getInt32(offset + entriesPerSector * 4, true);
  }

  // FAT 구축
  const fat: number[] = [];
  for (const fatSectorIdx of difat) {
    if (fatSectorIdx < 0) break;
    const offset = sectorOffset(fatSectorIdx, sectorSize);
    for (let i = 0; i < sectorSize / 4; i++) {
      fat.push(view.getInt32(offset + i * 4, true));
    }
  }

  // 디렉토리 엔트리 읽기
  const dirData = readSectorChain(data, fat, firstDirSector, sectorSize);
  const entries = parseDirectoryEntries(dirData);

  // Mini FAT 구축
  let miniFat: number[] = [];
  if (firstMiniFatSector >= 0 && firstMiniFatSector !== ENDOFCHAIN) {
    const miniFatData = readSectorChain(data, fat, firstMiniFatSector, sectorSize);
    const miniFatView = new DataView(miniFatData.buffer, miniFatData.byteOffset, miniFatData.byteLength);
    for (let i = 0; i < miniFatData.length / 4; i++) {
      miniFat.push(miniFatView.getInt32(i * 4, true));
    }
  }

  // Mini stream (root entry의 데이터)
  let miniStream: Uint8Array = new Uint8Array(0);
  if (entries.length > 0 && entries[0].startSector >= 0) {
    miniStream = readSectorChain(data, fat, entries[0].startSector, sectorSize);
  }

  function getStreamByEntry(entry: OleEntry): Uint8Array {
    if (entry.size === 0) return new Uint8Array(0);

    if (entry.size < miniStreamCutoff) {
      // mini stream에서 읽기
      return readMiniSectorChain(miniStream, miniFat, entry.startSector, miniSectorSize, entry.size);
    } else {
      // 일반 섹터 체인에서 읽기
      const raw = readSectorChain(data, fat, entry.startSector, sectorSize);
      return raw.slice(0, entry.size);
    }
  }

  function getStream(name: string): Uint8Array {
    const entry = entries.find((e) => e.name === name && e.type === 2);
    if (!entry) {
      throw new HWPXError(`OLE stream not found: ${name}`);
    }
    return getStreamByEntry(entry);
  }

  function listStreams(): string[] {
    return entries.filter((e) => e.type === 2).map((e) => e.name);
  }

  return { entries, getStream, getStreamByEntry, listStreams };
}

function sectorOffset(sector: number, sectorSize: number): number {
  return HEADER_SIZE + sector * sectorSize;
}

function readSectorChain(
  data: Uint8Array,
  fat: number[],
  startSector: number,
  sectorSize: number,
): Uint8Array {
  const sectors: number[] = [];
  let sector = startSector;
  const maxSectors = fat.length; // 무한루프 방지

  while (sector >= 0 && sector !== ENDOFCHAIN && sectors.length < maxSectors) {
    sectors.push(sector);
    sector = fat[sector] ?? ENDOFCHAIN;
  }

  const result = new Uint8Array(sectors.length * sectorSize);
  for (let i = 0; i < sectors.length; i++) {
    const offset = sectorOffset(sectors[i], sectorSize);
    const chunk = data.subarray(offset, offset + sectorSize);
    result.set(chunk, i * sectorSize);
  }

  return result;
}

function readMiniSectorChain(
  miniStream: Uint8Array,
  miniFat: number[],
  startSector: number,
  miniSectorSize: number,
  size: number,
): Uint8Array {
  const sectors: number[] = [];
  let sector = startSector;
  const maxSectors = miniFat.length;

  while (sector >= 0 && sector !== ENDOFCHAIN && sectors.length < maxSectors) {
    sectors.push(sector);
    sector = miniFat[sector] ?? ENDOFCHAIN;
  }

  const result = new Uint8Array(sectors.length * miniSectorSize);
  for (let i = 0; i < sectors.length; i++) {
    const offset = sectors[i] * miniSectorSize;
    const chunk = miniStream.subarray(offset, offset + miniSectorSize);
    result.set(chunk, i * miniSectorSize);
  }

  return result.slice(0, size);
}

function parseDirectoryEntries(data: Uint8Array): OleEntry[] {
  const entries: OleEntry[] = [];
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  for (let offset = 0; offset + DIR_ENTRY_SIZE <= data.length; offset += DIR_ENTRY_SIZE) {
    const nameLen = view.getUint16(offset + 64, true);
    if (nameLen === 0) continue;

    // UTF-16LE 이름 디코딩
    const nameBytes = data.subarray(offset, offset + nameLen - 2); // -2: null terminator 제외
    let name = '';
    for (let i = 0; i < nameBytes.length; i += 2) {
      const code = nameBytes[i] | (nameBytes[i + 1] << 8);
      if (code === 0) break;
      name += String.fromCharCode(code);
    }

    const type = data[offset + 66];
    const startSector = view.getInt32(offset + 116, true);
    const size = view.getUint32(offset + 120, true);
    const leftSiblingId = view.getInt32(offset + 68, true);
    const rightSiblingId = view.getInt32(offset + 72, true);
    const childId = view.getInt32(offset + 76, true);

    entries.push({
      name,
      type,
      size,
      startSector,
      childId,
      leftSiblingId,
      rightSiblingId,
    });
  }

  return entries;
}
